import { z } from 'zod';

/**
 * Zod schemas and inferred TypeScript types for the card-vision service.
 *
 * These schemas validate the `ScanResponse` returned by the `scan_card` Edge
 * Function, which proxies the Python card-vision service's `/scan` endpoint
 * (photo → candidate card identification).
 *
 * Shell: (desktop) + (mobile) — vision is a staff-only tool; never used in the
 * kiosk shell (the Edge Function rejects anonymous sessions with 401).
 *
 * Tolerance note: the service may omit optional/nullable fields between model
 * versions, so anything not strictly required is `.nullish()` or defaulted.
 * `confidence` is left as a free string (the service sends 'high'/'medium'/'low'
 * today but the boundary should not hard-fail if that vocabulary widens).
 */

// ── Candidate ─────────────────────────────────────────────────────────────────

/**
 * A single candidate card match from the vision service.
 *
 * CRITICAL: `card_id` IS the tcgplayer_product_id (integer-as-text). It feeds
 * directly into `useCardMetadataBatch` and the cards/card_skus upsert as
 * `Number(card_id)` — no extra catalog round-trip is required.
 */
export const VisionCandidateSchema = z.object({
  /** tcgplayer_product_id as a string (e.g. "12345"). */
  card_id: z.string(),
  name: z.string(),
  set_id: z.string().nullish(),
  set_name: z.string(),
  /** Null for older/promo singles that carry no TCGplayer collector number. */
  collector_number: z.string().nullish(),
  rarity: z.string().nullish(),
  image_url: z.string(),
  /** Combined ranking score (0–1). */
  final_score: z.number(),
  /** Image-similarity component of the score. */
  image_score: z.number(),
  /** Text/OCR boost component of the score. */
  text_boost: z.number(),
  /** Free-form confidence label ('high' | 'medium' | 'low' today). */
  confidence: z.string(),
  /** Human-readable explanations for why this card was matched. */
  reasons: z.array(z.string()).default([]),
  artwork_similarity: z.number().nullish(),
  types: z.array(z.string()).default([]),
});
export type VisionCandidate = z.infer<typeof VisionCandidateSchema>;

// ── Scan response ─────────────────────────────────────────────────────────────

/** Full response shape from the `scan_card` Edge Function / vision `/scan`. */
export const VisionScanResponseSchema = z.object({
  /** True when the top match is confident enough to skip manual confirmation. */
  auto_confirmed: z.boolean(),
  top_match: VisionCandidateSchema.nullable(),
  alternatives: z.array(VisionCandidateSchema).default([]),
  /** Raw OCR fields extracted from the photo (shape varies; passthrough). */
  ocr_extracted: z.record(z.string(), z.unknown()).default({}),
  detected_type: z.string().nullish(),
  type_confidence: z.number().nullish(),
  /** Detected era / game family bucket. */
  era: z.string(),
  debug: z.record(z.string(), z.unknown()).nullish(),
});
export type VisionScanResponse = z.infer<typeof VisionScanResponseSchema>;
